import { floatToPCM16, resample, TARGET_RATE } from './pcm'

/** Served from public/, so it is a plain URL at the site root. */
const WORKLET_URL = '/pcm-capture.worklet.js'

export interface Capture {
  /** Live mic spectrum, for the orb. fftSize 512 → 256 bins. */
  analyser: AnalyserNode
  /** Stops the microphone and turns the phone's recording indicator off. */
  stop: () => void
}

interface CaptureOptions {
  /**
   * Called with each block, already at 16 kHz and 16-bit. Return value ignored;
   * the caller decides whether to actually send it.
   */
  onBlock: (pcm: Int16Array) => void
}

/**
 * Opens the microphone and streams it as PCM blocks.
 *
 * Blocks are delivered whether or not the caller intends to send them — the
 * speech gate suppresses *sending*, not capturing, so the orb keeps reacting to
 * the room while the agent is talking.
 */
export async function startCapture(
  ctx: AudioContext,
  { onBlock }: CaptureOptions,
): Promise<Capture> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
      // A hint only. The browser will usually ignore it and run the context at
      // 48 kHz, which is why every block is resampled below rather than trusted.
      sampleRate: TARGET_RATE,
    },
  })

  await ctx.audioWorklet.addModule(WORKLET_URL)

  const source = ctx.createMediaStreamSource(stream)
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 512
  source.connect(analyser)

  const node = new AudioWorkletNode(ctx, 'pcm-capture')
  node.port.onmessage = (e: MessageEvent<Float32Array>) => {
    onBlock(floatToPCM16(resample(e.data, ctx.sampleRate, TARGET_RATE)))
  }
  source.connect(node)
  // Deliberately not connected to ctx.destination. A ScriptProcessor had to be,
  // or it was never pulled; an AudioWorklet is pulled regardless, and routing
  // the microphone to the speakers would hand the agent a feedback loop.

  return {
    analyser,
    stop() {
      node.port.onmessage = null
      try {
        node.disconnect()
        source.disconnect()
        analyser.disconnect()
      } catch {
        // Already torn down.
      }
      stream.getTracks().forEach((t) => t.stop())
    },
  }
}
